import {useSelector} from '@xstate/react';
import {ReactNode, createContext, useContext} from 'react';
import {Actor, SnapshotFrom} from 'xstate';
import {eventRegistrationMachine} from './eventRegistrationMachine';

type Logic = typeof eventRegistrationMachine;
type EventRegistrationActor = Actor<Logic>;

const EventRegistrationActorContext =
  createContext<EventRegistrationActor | null>(null);

interface EventRegistrationProviderProps {
  actor: EventRegistrationActor;
  children: ReactNode;
}

/**
 * Exposes an `eventRegistrationMachine` actor to descendants. The caller owns
 * the actor's lifecycle (via `useActorRef` at the route level) so it can also
 * send events in response to external state changes (e.g. loader data
 * updates).
 */
export function EventRegistrationProvider({
  actor,
  children,
}: EventRegistrationProviderProps) {
  return (
    <EventRegistrationActorContext.Provider value={actor}>
      {children}
    </EventRegistrationActorContext.Provider>
  );
}

/**
 * Returns the actor when inside a provider, or `null` when not. Use this when
 * a component needs to dispatch on whether the machine applies (e.g. the
 * registration sidebar wrapper).
 */
export function useEventRegistrationActor(): EventRegistrationActor | null {
  return useContext(EventRegistrationActorContext);
}

/**
 * Subscribes to the actor's snapshot. Throws when no provider is mounted —
 * use only from components that are guaranteed to render inside the provider.
 */
export function useEventRegistrationState(): SnapshotFrom<Logic> {
  const actor = useContext(EventRegistrationActorContext);
  if (!actor) {
    throw new Error(
      'useEventRegistrationState must be used inside <EventRegistrationProvider>'
    );
  }
  return useSelector(actor, (s) => s);
}
